/**
 * Generates or retrieves a unique ID slug for a header element.
 * @param {HTMLElement} el 
 * @param {Set<string>} existingIds - Cache of already assigned IDs to prevent collisions
 * @returns {string}
 */
function getAnchor(el, existingIds) {
    // 1. Check for a direct child anchor with an ID
    const childAnchor = el.querySelector(':scope > a[id]');
    if (childAnchor?.id) {
        existingIds.add(childAnchor.id);
        return childAnchor.id;
    }

    // 2. Check if the element itself has an ID
    if (el.id) {
        existingIds.add(el.id);
        return el.id;
    }

    // 3. Generate a clean slug ID if none exists
    const baseSlug = el.textContent
        .trim()
        .toLowerCase()
        .normalize('NFD') // Normalizes accents/diacritics (e.g. "é" -> "e")
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'header';

    // 4. Handle collisions (e.g., duplicate header text)
    let slug = baseSlug;
    let counter = 1;

    while (existingIds.has(slug) || document.getElementById(slug)) {
        slug = `${baseSlug}-${counter++}`;
    }

    el.id = slug;
    existingIds.add(slug);
    return slug;
}

/**
 * Dynamically builds navigation links for specific headers and injects them into target lists.
 * @param {Object} [options]
 * @param {string} [options.listSelector='.header-list'] - Target containers for the navigation
 * @param {string} [options.headerSelector='h2'] - Headers to scan and link to
 * @param {string} [options.separatorText=' | '] - Visual separator between links
 */
export function initHeaderLinks({
    listSelector = '.header-list',
    headerSelector = 'h2',
    separatorText = ' | '
} = {}) {
    const lists = document.querySelectorAll(listSelector);
    if (lists.length === 0) return;

    const headers = document.querySelectorAll(headerSelector);
    if (headers.length === 0) return;

    // Collect all pre-existing IDs on the page to prevent duplicate ID bugs
    const existingIds = new Set(
        Array.from(document.querySelectorAll('[id]'), el => el.id)
    );

    const fragment = document.createDocumentFragment();
    let linkCount = 0;

    headers.forEach((header) => {
        const anchor = getAnchor(header, existingIds);

        // Add separator before subsequent items
        if (linkCount > 0 && separatorText) {
            const separator = document.createElement('span');
            separator.className = 'header-list-separator';
            separator.ariaHidden = 'true'; // Accessibility improvement
            separator.textContent = separatorText;
            fragment.appendChild(separator);
        }

        // Create link element
        const link = document.createElement('a');
        link.href = `#${anchor}`;
        link.textContent = header.textContent.trim();
        fragment.appendChild(link);

        linkCount++;
    });

    // Inject into all target list containers
    lists.forEach((list, index) => {
        const content = index === 0 ? fragment : fragment.cloneNode(true);
        list.replaceChildren(content); // Replaces existing content cleanly
    });
}